import { MEAL_TIME, type FoodPrefs, type Meal, type MealInput, type Vote } from '@shh/shared';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { firebase } from '../lib/firebase';
import { useFamily } from './family';

/**
 * Essensplan (Küche 3): read directly (the whole family, the wall too), written through the
 * server (Plus, votes per person). Days without a meal simply have no document.
 */
export function useMeals(from: string, to: string): Map<string, Meal> | undefined {
  const { householdId } = useFamily();
  // the meals remember their range: right after "Nächste Woche" the old week must not pass for
  // the new one – "Offene Tage vorschlagen" offered planned days that way (17.10.)
  const range = `${from}|${to}`;
  const [meals, setMeals] = useState<{ range: string; map: Map<string, Meal> }>();
  useEffect(
    () =>
      onSnapshot(
        query(
          collection(firebase().db, `households/${householdId}/meals`),
          where('date', '>=', from),
          where('date', '<=', to),
        ),
        (s) => setMeals({ range, map: new Map(s.docs.map((d) => [d.id, d.data() as Meal])) }),
        () => setMeals((m) => (m?.range === range ? m : { range, map: new Map() })),
      ),
    [householdId, from, to, range],
  );
  return meals?.range === range ? meals.map : undefined;
}

/** The family's dinner time, or 18:30. */
export const useMealTime = () => useFamily().household?.mealTime ?? MEAL_TIME;

const call = async <T>(name: string, data: unknown): Promise<T> =>
  (await httpsCallable(firebase().functions, name)(data)).data as T;

export const planMeal = (m: MealInput) => call('planMeal', m);
export const clearMeal = (date: string) => call('clearMeal', { date });
export const voteMeal = (date: string, memberId: string, vote: Vote | null) =>
  call('voteMeal', { date, memberId, vote });
export const setMealTime = (time: string) => call('setMealTime', { time });
export const setFoodPrefs = (memberId: string, food: FoodPrefs) =>
  call('setFoodPrefs', { memberId, ...food });
